import { BookTemplate, Send } from 'lucide-react-native';
import { useCallback, useState } from 'react';
import { Platform, Pressable, StyleSheet } from 'react-native';
import { Box, HStack, Text } from '../../../components/primitives';
import { FormInput } from '../../../components/primitives/FormInput';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { fieldPlaceholderColor } from '../../../theme/typography';

interface MessageInputBarProps {
  windowIsOpen: boolean;
  isSending: boolean;
  onSend: (text: string) => void;
  onOpenTemplates: () => void;
}

export function MessageInputBar({
  windowIsOpen,
  isSending,
  onSend,
  onOpenTemplates,
}: MessageInputBarProps) {
  const { t } = useTranslation();
  const [text, setText] = useState('');

  const handleSend = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed || isSending) return;
    onSend(trimmed);
    setText('');
  }, [text, isSending, onSend]);

  if (!windowIsOpen) {
    return (
      <Box
        paddingX="md"
        paddingY="sm"
        backgroundColor={colors.background.secondary}
        borderTopWidth={1}
        borderTopColor={colors.border.muted}
      >
        <Pressable onPress={onOpenTemplates} style={styles.templateButton}>
          <HStack spacing="sm" align="center" justify="center">
            <BookTemplate size={18} color={colors.primary.neon} />
            <Text variant="body" color={colors.primary.neon} style={styles.templateText}>
              {t('whatsapp.window.useTemplate')}
            </Text>
          </HStack>
        </Pressable>
        <Text variant="caption" color={colors.text.tertiary} style={styles.hint}>
          {t('whatsapp.window.hint')}
        </Text>
      </Box>
    );
  }

  return (
    <Box
      paddingX="md"
      paddingY="sm"
      backgroundColor={colors.background.secondary}
      borderTopWidth={1}
      borderTopColor={colors.border.muted}
    >
      <HStack spacing="sm" align="end" style={styles.inputRow}>
        <Pressable onPress={onOpenTemplates} style={styles.iconButton}>
          <BookTemplate size={20} color={colors.text.tertiary} />
        </Pressable>
        <FormInput
          style={styles.input}
          value={text}
          onChangeText={setText}
          placeholder={t('whatsapp.input.placeholder')}
          placeholderTextColor={fieldPlaceholderColor}
          multiline
          maxLength={4096}
          autoFocus={Platform.OS === 'web'}
          onSubmitEditing={Platform.OS === 'web' ? handleSend : undefined}
          blurOnSubmit={Platform.OS === 'web'}
        />
        <Pressable
          onPress={handleSend}
          disabled={!text.trim() || isSending}
          style={[styles.sendButton, (!text.trim() || isSending) && styles.sendButtonDisabled]}
        >
          <Send size={18} color={text.trim() ? colors.text.inverse : colors.text.disabled} />
        </Pressable>
      </HStack>
    </Box>
  );
}

const styles = StyleSheet.create({
  inputRow: {
    minHeight: 44,
  },
  iconButton: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  input: {
    flex: 1,
    backgroundColor: colors.background.input,
    borderRadius: tokens.radius.lg,
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.sm,
    color: colors.text.primary,
    fontSize: tokens.fontSize.base,
    maxHeight: 120,
    borderWidth: 1,
    borderColor: colors.border.input,
  },
  sendButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary.main,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sendButtonDisabled: {
    backgroundColor: colors.background.tertiary,
  },
  templateButton: {
    backgroundColor: colors.primary.muted,
    borderRadius: tokens.radius.md,
    paddingVertical: tokens.spacing.md,
    borderWidth: 1,
    borderColor: colors.primary.subtle,
  },
  templateText: {
    fontWeight: tokens.fontWeight.semibold,
  },
  hint: {
    textAlign: 'center',
    marginTop: tokens.spacing.xs,
  },
});
